import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      food: 'Food',
      hospitals: 'Hospitals',
      reels: 'Reels',
      recommendations: 'Recommendations',
      notifications: {
        title: "Notifications",
        subtitle: "Reminders and important updates",
        noNotifications: "You have no notifications",
        noNotificationsSubtitle: "Notifications will appear here",
        new: "New",
        types: {
          food: {
            breakfast: {
              title: "Breakfast reminder",
              message: "Time for your iron-rich breakfast"
            },
            lunch: {
              title: "Lunch reminder",
              message: "Don't forget to include green leafy vegetables in your lunch"
            },
            dinner: {
              title: "Dinner reminder",
              message: "A portion of red meat will help maintain your iron levels"
            }
          },
          medication: {
            title: "Take supplement",
            message: "Time to take your iron supplement"
          },
          update: {
            title: "New recommendation",
            message: "We've added new iron-rich recipes"
          }
        }
      },
      healthPoints: 'Health Points',
      // Recipe details translations
      benefits: 'Benefits',
      ingredients: 'Ingredients',
      instructions: 'Instructions',
      viewAllInstructions: 'View all instructions',
      recipeDetail: 'Recipe Detail',
      viewRecipe: 'View recipe',
      moreItems: 'more',
      noIngredientsRegistered: 'No ingredients registered',
      // Hospital tab translations
      nearbyHealthCenters: 'Nearby Health Centers',
      howToGet: 'How to get there',
      noHealthCentersAvailable: 'No health centers available in your area',
      loadingHealthCenters: 'Loading health centers...',
      // Reels tab translations
      informationalVideos: 'Informational Videos',
      loadingVideos: 'Loading videos...',
      noVideosAvailable: 'No videos available',
      viewHistory: 'View history',
      resultsInProfile: 'Your test results are saved in your profile',
      // Food recommendations translations
      recipesForAnemia: 'These recipes are specially selected to fight anemia and increase your iron levels.',
      recipesForRegion: 'These recipes are specially selected to fight anemia and increase your iron levels, with special emphasis on typical dishes from',
      setLocation: 'set up your location in your profile',
      toSeeRelevantRecipes: 'To see more relevant recipes for your region, we invite you to',
      // Recipe translations kept from before
      recipes: {
        'Ensalada de espinacas con fresas': 'Spinach and Strawberry Salad',
        'Una ensalada rica en hierro y vitamina C para mejorar la absorción del hierro.': 'A salad rich in iron and vitamin C to improve iron absorption.',
        'Hígado salteado con cebolla y manzana': 'Sautéed Liver with Onion and Apple',
        'Una receta rica en hierro hemo, que es más fácilmente absorbido por el cuerpo.': 'A recipe rich in heme iron, which is more easily absorbed by the body.',
        'Lentejas con verduras y limón': 'Lentils with Vegetables and Lemon',
        'Un plato nutritivo rico en hierro, proteínas y vitamina C.': 'A nutritious dish rich in iron, protein, and vitamin C.',
        'Batido de remolacha y frutos rojos': 'Beetroot and Red Berries Smoothie',
        'Bebida energética que ayuda a aumentar los niveles de hemoglobina.': 'Energy drink that helps increase hemoglobin levels.'
      },
      // Common UI translations
      error: 'Error',
      loading: 'Loading...',
      retry: 'Retry',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      back: 'Back',
      profilePicture: 'Profile Picture',
      yes: 'Yes',
      no: 'No',
      unknown: 'Unknown',
      question: 'Question',
      of: 'of',
      profiles: 'Profiles',
      // Profile translations
      editProfile: 'Edit Profile',
      saveChanges: 'Save Changes',
      profileUpdated: 'Profile updated successfully',
      // Error messages
      errorLoadingData: 'Error loading data',
      tryAgainLater: 'Please try again later',
      networkError: 'Network error',
      // Success messages
      success: 'Success',
      changesSaved: 'Changes saved successfully',
      // Validation messages
      requiredField: 'This field is required',
      invalidEmail: 'Invalid email address',
      invalidPhone: 'Invalid phone number',
      // Home screen translations
      home: {
        title: 'Anemia Detective',
        subtitle: 'Quick assessment and recommendations for people with possible anemia',
        questionnaire: {
          title: 'Symptom Questionnaire',
          description: '5 quick questions about anemia signs'
        },
        photoEvaluation: {
          title: 'Photo Evaluation',
          description: 'Visual analysis of eyes and nails'
        },
        recommendations: 'Access recommendations',
        disclaimer: 'This app does not replace a medical diagnosis. Consult a professional for a definitive analysis.'
      },
      questionnaire: 'Questionnaire',
      questionnaireQuestions: {
        '1': "Do you frequently feel tired without an apparent reason?",
        '2': "Does your skin look paler than normal?",
        '3': "Do you often feel dizzy or weak?",
        '4': "Do your nails have a pale tone or break easily?",
        '5': "Is the inside of your eyelids whiter than normal?"
      },
      results: {
        title: "Result",
        saveSuccess: "Results saved successfully",
        saveError: "Error saving results: {{error}}",
        nextSteps: "Continue with next steps",
        backToHome: "Back to home",
        highRisk: "High risk",
        moderateRisk: "Moderate risk",
        lowRisk: "Low risk",
        healthy: "Healthy",
        messages: {
          healthy: "Your evaluation is favorable, but maintaining a balanced diet is always important to prevent anemia.",
          moderate: "Your evaluation shows moderate risk. We suggest improving your diet and including more iron-rich foods.",
          high: "Based on your evaluation, there are signs that suggest possible anemia. We recommend reviewing an iron-rich diet and consulting a doctor.",
          short: {
            healthy: "You are within the healthy range.",
            moderate: "Your level is slightly low.",
            high: "Your level is critically low."
          }
        }
      },
      profile: {
        title: "Profiles",
        savedProfiles: "Saved profiles",
        addProfile: "Add profile",
        anemiaStatus: "Anemia status",
        riskLevels: {
          high: "High probability",
          moderate: "Moderate risk",
          low: "Low risk"
        },
        location: {
          country: "Country",
          region: "Region",
          selectCountry: "Select country",
          selectRegion: "Select region",
          loading: "Loading...",
          updateSuccess: "Location updated",
          updateError: "Error updating location: {{error}}",
          loadCountriesError: "Error loading countries: {{error}}",
          loadRegionsError: "Error loading regions: {{error}}",
          title: "Location"
        },
        additionalOptions: {
          title: "Additional options",
          startNewEvaluation: "Start new evaluation",
          signOut: "Sign out"
        },
        newProfile: "New profile",
        name: "Name",
        fullName: "Full name",
        age: "Age",
        saveProfile: "Save profile",
        noProfiles: "No saved profiles",
        loginToManage: "Sign in to save and manage more profiles",
        profileSelected: "Profile \"{{name}}\" selected",
        invalidProfileId: "Invalid profile ID",
        years: "years",
        delete: "Delete",
        edit: "Edit",
        auth: {
          title: "Sign in (optional)",
          description: "You can sign in to save your profile across devices.",
          signInWithGoogle: "Continue with Google",
          signedInAs: "Signed in as:",
          signOut: "Sign out"
        },
        evaluationHistory: {
          title: "Evaluation History",
          loading: "Loading history...",
          noEvaluations: "No previous evaluations for this profile",
          viewAll: "View all",
          loadError: "Error loading history: {{error}}",
          location: {
            latitude: "Lat",
            longitude: "Lon",
            notAvailable: "N/A"
          }
        },
        selector: {
          loading: "Loading profiles...",
          noProfiles: "No profiles available",
          selectProfile: "Select profile",
          ageFormat: "{{age}} years"
        },
        healthPoints: {
          title: "Health Points",
          level: "Level {{level}}",
          progress: "Progress",
          points: "{{points}} points",
          description: "Earn points by consuming healthy foods"
        },
        newEvaluation: "New evaluation",
        performNewEvaluation: "Perform new evaluation"
      },
      photoEvaluation: {
        title: "Photo Evaluation",
        errors: {
          loadSampleImage: "Error loading sample image",
          analyzeImage: "Error analyzing the image",
          takeEyePhoto: "Please take a photo of your eye first",
          processEyeImage: "An error occurred while processing the eye image",
          takeNailPhoto: "Please take a photo of your nails first",
          processImage: "An error occurred while processing the image"
        },
        analysisDetails: {
          title: "Analysis Details",
          description: "Detailed results of image analysis",
          viewDetails: "View analysis details",
          close: "Close",
          tabs: {
            eyes: "Eyes",
            nails: "Nails"
          },
          conjunctiva: {
            title: "Conjunctiva Analysis",
            detectedPredictions: "Detected predictions:",
            noElements: "No elements detected in the image",
            confidence: "confidence",
            location: "Location"
          },
          nails: {
            title: "Nail Analysis",
            detectedPredictions: "Detected predictions:",
            noElements: "No elements detected in the image",
            confidence: "confidence",
            location: "Location"
          },
          processingTime: "Processing time",
          model: "Model"
        },
        profileSelect: {
          title: "Select a profile",
          description: "Select the profile for which you want to perform the evaluation or continue without logging in",
          manageProfiles: "Manage profiles",
          continueWithoutLogin: "Continue without logging in"
        },
        sampleImages: {
          title: "Sample images:",
          alt: "Sample {{number}}",
          hide: "Hide sample images",
          show: "View sample images"
        },
        buttons: {
          continue: "Continue",
          analyze: "Analyze images"
        },
        processing: {
          title: "Analyzing images",
          description: "Our system is processing the photographs to detect possible signs of anemia..."
        },
        camera: {
          eyeTitle: "Eye Photography",
          eyeInstructions: "Take a photo of the inside of your lower eyelid. Gently pull down the eyelid to show the inner area.",
          eyeAlt: "Captured eye",
          nailTitle: "Nail Photography",
          nailInstructions: "Take a clear photo of your nails with good lighting. Place your hand on a flat surface.",
          nailAlt: "Captured nails",
          tapToCapture: "Tap to capture image",
          loadAnotherImage: "Load another image"
        },
        modelSelector: {
          title: "Select Analysis Model:",
          model2: "Model 2 (Detect)",
          model3Server: "Model 3 (Server)",
          model3Live: "Model 3 (Live)",
          currentModel: "Current model:",
          show: "⚙️ Seleccionar modelo",
          hide: "⚙️ Ocultar selección"
        }
      }
    }
  },
  es: {
    translation: {
      food: 'Comidas',
      hospitals: 'Hospitales',
      reels: 'Videos',
      recommendations: 'Recomendaciones',
      notifications: {
        title: "Notificaciones",
        subtitle: "Recordatorios y actualizaciones importantes",
        noNotifications: "No tienes notificaciones",
        noNotificationsSubtitle: "Las notificaciones aparecerán aquí",
        new: "Nuevo",
        types: {
          food: {
            breakfast: {
              title: "Recordatorio de desayuno",
              message: "Es hora de tu desayuno rico en hierro"
            },
            lunch: {
              title: "Recordatorio de almuerzo",
              message: "No olvides incluir verduras de hoja verde en tu almuerzo"
            },
            dinner: {
              title: "Recordatorio de cena",
              message: "Una porción de carne roja te ayudará a mantener tus niveles de hierro"
            }
          },
          medication: {
            title: "Tomar suplemento",
            message: "Es momento de tomar tu suplemento de hierro"
          },
          update: {
            title: "Nueva recomendación",
            message: "Hemos agregado nuevas recetas ricas en hierro"
          }
        }
      },
      healthPoints: 'Puntos de Salud',
      // Recipe details translations
      benefits: 'Beneficios',
      ingredients: 'Ingredientes',
      instructions: 'Instrucciones',
      viewAllInstructions: 'Ver todas las instrucciones',
      recipeDetail: 'Detalle de Receta',
      viewRecipe: 'Ver receta',
      moreItems: 'más',
      noIngredientsRegistered: 'No hay ingredientes registrados',
      // Hospital tab translations
      nearbyHealthCenters: 'Centros de salud cercanos',
      howToGet: 'Cómo llegar',
      noHealthCentersAvailable: 'No hay centros de salud disponibles en tu zona',
      loadingHealthCenters: 'Cargando centros de salud...',
      // Reels tab translations
      informationalVideos: 'Videos informativos',
      loadingVideos: 'Cargando videos...',
      noVideosAvailable: 'No hay videos disponibles',
      viewHistory: 'Ver historial',
      resultsInProfile: 'Los resultados de tus análisis están guardados en tu perfil',
      // Food recommendations translations
      recipesForAnemia: 'Estas recetas están especialmente seleccionadas para combatir la anemia y aumentar tus niveles de hierro.',
      recipesForRegion: 'Estas recetas están especialmente seleccionadas para combatir la anemia y aumentar tus niveles de hierro, con especial énfasis en platillos típicos de',
      setLocation: 'configurar tu ubicación en tu perfil',
      toSeeRelevantRecipes: 'Para ver recetas más relevantes para tu región, te invitamos a',
      // Recipe translations kept in original Spanish
      recipes: {
        'Ensalada de espinacas con fresas': 'Ensalada de espinacas con fresas',
        'Una ensalada rica en hierro y vitamina C para mejorar la absorción del hierro.': 'Una ensalada rica en hierro y vitamina C para mejorar la absorción del hierro.',
        'Hígado salteado con cebolla y manzana': 'Hígado salteado con cebolla y manzana',
        'Una receta rica en hierro hemo, que es más fácilmente absorbido por el cuerpo.': 'Una receta rica en hierro hemo, que es más fácilmente absorbido por el cuerpo.',
        'Lentejas con verduras y limón': 'Lentejas con verduras y limón',
        'Un plato nutritivo rico en hierro, proteínas y vitamina C.': 'Un plato nutritivo rico en hierro, proteínas y vitamina C.',
        'Batido de remolacha y frutos rojos': 'Batido de remolacha y frutos rojos',
        'Bebida energética que ayuda a aumentar los niveles de hemoglobina.': 'Bebida energética que ayuda a aumentar los niveles de hemoglobina.'
      },
      // Common UI translations
      error: 'Error',
      loading: 'Cargando...',
      retry: 'Reintentar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      search: 'Buscar',
      filter: 'Filtrar',
      sort: 'Ordenar',
      back: 'Atrás',
      profilePicture: 'Foto de perfil',
      yes: 'Sí',
      no: 'No',
      unknown: 'Desconocido',
      question: 'Pregunta',
      of: 'de',
      profiles: 'Perfiles',
      // Profile translations
      editProfile: 'Editar Perfil',
      saveChanges: 'Guardar Cambios',
      profileUpdated: 'Perfil actualizado exitosamente',
      // Error messages
      errorLoadingData: 'Error al cargar los datos',
      tryAgainLater: 'Por favor, intente nuevamente más tarde',
      networkError: 'Error de red',
      // Success messages
      success: 'Éxito',
      changesSaved: 'Cambios guardados exitosamente',
      // Validation messages
      requiredField: 'Este campo es requerido',
      invalidEmail: 'Dirección de correo electrónico inválida',
      invalidPhone: 'Número de teléfono inválido',
      // Home screen translations
      home: {
        title: 'Anemia Detective',
        subtitle: 'Evaluación rápida y recomendaciones para personas con posible anemia',
        questionnaire: {
          title: 'Cuestionario de síntomas',
          description: '5 preguntas rápidas sobre signos de anemia'
        },
        photoEvaluation: {
          title: 'Evaluación por fotos',
          description: 'Análisis visual de ojos y uñas'
        },
        recommendations: 'Acceder a recomendaciones',
        disclaimer: 'Esta app no reemplaza un diagnóstico médico. Consulte a un profesional para un análisis definitivo.'
      },
      questionnaire: 'Cuestionario',
      questionnaireQuestions: {
        '1': "¿Sientes cansancio frecuente sin motivo aparente?",
        '2': "¿Tu piel luce más pálida de lo normal?",
        '3': "¿Sientes mareos o debilidad con frecuencia?",
        '4': "¿Tus uñas tienen un tono pálido o se quiebran fácilmente?",
        '5': "¿El interior de tus párpados se ve más blanco de lo normal?"
      },
      results: {
        title: "Resultado",
        saveSuccess: "Resultados guardados correctamente",
        saveError: "Error al guardar resultados: {{error}}",
        nextSteps: "Continuar con los siguientes pasos",
        backToHome: "Volver al inicio",
        highRisk: "Alto riesgo",
        moderateRisk: "Riesgo moderado",
        lowRisk: "Bajo riesgo",
        healthy: "Saludable",
        messages: {
          healthy: "Tu evaluación es favorable, pero mantener una dieta balanceada siempre es importante para prevenir la anemia.",
          moderate: "Tu evaluación muestra un riesgo moderado. Te sugerimos mejorar tu alimentación e incluir más alimentos ricos en hierro.",
          high: "Basado en tu evaluación, hay signos que sugieren posible anemia. Recomendamos revisar una dieta rica en hierro y consultar a un médico.",
          short: {
            healthy: "Estás dentro del rango saludable.",
            moderate: "Tu nivel es ligeramente bajo.",
            high: "Tu nivel es críticamente bajo."
          }
        }
      },
      profile: {
        title: "Perfiles",
        savedProfiles: "Perfiles guardados",
        addProfile: "Añadir perfil",
        anemiaStatus: "Estado de anemia",
        riskLevels: {
          high: "Alta probabilidad",
          moderate: "Riesgo moderado",
          low: "Bajo riesgo"
        },
        location: {
          country: "País",
          region: "Región",
          selectCountry: "Seleccionar país",
          selectRegion: "Seleccionar región",
          loading: "Cargando...",
          updateSuccess: "Ubicación actualizada",
          updateError: "Error al actualizar ubicación: {{error}}",
          loadCountriesError: "Error al cargar países: {{error}}",
          loadRegionsError: "Error al cargar regiones: {{error}}",
          title: "Ubicación"
        },
        additionalOptions: {
          title: "Opciones adicionales",
          startNewEvaluation: "Iniciar nueva evaluación",
          signOut: "Cerrar sesión"
        },
        newProfile: "Nuevo perfil",
        name: "Nombre",
        fullName: "Nombre completo",
        age: "Edad",
        saveProfile: "Guardar perfil",
        noProfiles: "No hay perfiles guardados",
        loginToManage: "Inicia sesión para guardar y gestionar más perfiles",
        profileSelected: "Perfil \"{{name}}\" seleccionado",
        invalidProfileId: "ID de perfil inválido",
        years: "años",
        delete: "Eliminar",
        edit: "Editar",
        auth: {
          title: "Iniciar sesión (opcional)",
          description: "Puedes iniciar sesión para guardar tu perfil entre dispositivos.",
          signInWithGoogle: "Continuar con Google",
          signedInAs: "Sesión iniciada como:",
          signOut: "Cerrar sesión"
        },
        evaluationHistory: {
          title: "Historial de Evaluaciones",
          loading: "Cargando historial...",
          noEvaluations: "No hay evaluaciones previas para este perfil",
          viewAll: "Ver todas",
          loadError: "Error al cargar historial: {{error}}",
          location: {
            latitude: "Lat",
            longitude: "Lon",
            notAvailable: "N/D"
          }
        },
        selector: {
          loading: "Cargando perfiles...",
          noProfiles: "No hay perfiles disponibles",
          selectProfile: "Seleccionar perfil",
          ageFormat: "{{age}} años"
        },
        healthPoints: {
          title: "Puntos de Salud",
          level: "Nivel {{level}}",
          progress: "Progreso",
          points: "{{points}} puntos",
          description: "Gana puntos consumiendo alimentos saludables"
        },
        newEvaluation: "Nueva evaluación",
        performNewEvaluation: "Realizar nueva evaluación"
      },
      photoEvaluation: {
        title: "Evaluación por fotos",
        errors: {
          loadSampleImage: "Error al cargar la imagen de muestra",
          analyzeImage: "Error al analizar la imagen",
          takeEyePhoto: "Por favor tome una foto de su ojo primero",
          processEyeImage: "Ocurrió un error al procesar la imagen del ojo",
          takeNailPhoto: "Por favor tome una foto de sus uñas primero",
          processImage: "Ocurrió un error al procesar la imagen"
        },
        analysisDetails: {
          title: "Detalles del análisis",
          description: "Resultados detallados del análisis de imágenes",
          viewDetails: "Ver detalles del análisis",
          close: "Cerrar",
          tabs: {
            eyes: "Ojos",
            nails: "Uñas"
          },
          conjunctiva: {
            title: "Análisis de conjuntiva",
            detectedPredictions: "Predicciones detectadas:",
            noElements: "No se detectaron elementos en la imagen",
            confidence: "confianza",
            location: "Ubicación"
          },
          nails: {
            title: "Análisis de uñas",
            detectedPredictions: "Predicciones detectadas:",
            noElements: "No se detectaron elementos en la imagen",
            confidence: "confianza",
            location: "Ubicación"
          },
          processingTime: "Tiempo de procesamiento",
          model: "Modelo"
        },
        profileSelect: {
          title: "Seleccione un perfil",
          description: "Seleccione el perfil para el cual desea realizar la evaluación o continúe sin iniciar sesión",
          manageProfiles: "Gestionar perfiles",
          continueWithoutLogin: "Continuar sin iniciar sesión"
        },
        sampleImages: {
          title: "Imágenes de muestra:",
          alt: "Muestra {{number}}",
          hide: "Ocultar imágenes de muestra",
          show: "Ver imágenes de muestra"
        },
        buttons: {
          continue: "Continuar",
          analyze: "Analizar imágenes"
        },
        processing: {
          title: "Analizando imágenes",
          description: "Nuestro sistema está procesando las fotografías para detectar posibles signos de anemia..."
        },
        camera: {
          eyeTitle: "Fotografía de ojo",
          eyeInstructions: "Tome una foto del interior de su párpado inferior. Baje suavemente el párpado para mostrar el área interna.",
          eyeAlt: "Ojo capturado",
          nailTitle: "Fotografía de uñas",
          nailInstructions: "Tome una foto clara de sus uñas con buena iluminación. Coloque su mano en una superficie plana.",
          nailAlt: "Uñas capturadas",
          tapToCapture: "Toque para capturar imagen",
          loadAnotherImage: "Cargar otra imagen"
        },
        modelSelector: {
          title: "Seleccionar Modelo de Análisis:",
          model2: "Modelo 2 (Detect)",
          model3Server: "Modelo 3 (Servidor)",
          model3Live: "Modelo 3 (Live)",
          currentModel: "Modelo actual:",
          show: "⚙️ Seleccionar modelo",
          hide: "⚙️ Ocultar selección"
        }
      }
    }
  },
  pt: {
    translation: {
      food: 'Comidas',
      hospitals: 'Hospitais',
      reels: 'Vídeos',
      recommendations: 'Recomendações',
      notifications: {
        title: "Notificações",
        subtitle: "Lembretes e atualizações importantes",
        noNotifications: "Você não tem notificações",
        noNotificationsSubtitle: "As notificações aparecerão aqui",
        new: "Novo",
        types: {
          food: {
            breakfast: {
              title: "Lembrete de café da manhã",
              message: "Hora do seu café da manhã rico em ferro"
            },
            lunch: {
              title: "Lembrete de almoço",
              message: "Não se esqueça de incluir vegetais folhosos verdes no seu almoço"
            },
            dinner: {
              title: "Lembrete de jantar",
              message: "Uma porção de carne vermelha ajudará a manter seus níveis de ferro"
            }
          },
          medication: {
            title: "Tomar suplemento",
            message: "Hora de tomar seu suplemento de ferro"
          },
          update: {
            title: "Nova recomendação",
            message: "Adicionamos novas receitas ricas em ferro"
          }
        }
      },
      healthPoints: 'Pontos de Saúde',
      // Recipe details translations
      benefits: 'Benefícios',
      ingredients: 'Ingredientes',
      instructions: 'Instruções',
      viewAllInstructions: 'Ver todas as instruções',
      recipeDetail: 'Detalhe da Receita',
      viewRecipe: 'Ver receita',
      moreItems: 'mais',
      noIngredientsRegistered: 'Não há ingredientes registrados',
      // Hospital tab translations
      nearbyHealthCenters: 'Centros de saúde próximos',
      howToGet: 'Como chegar',
      noHealthCentersAvailable: 'Não há centros de saúde disponíveis na sua área',
      loadingHealthCenters: 'Carregando centros de saúde...',
      // Reels tab translations
      informationalVideos: 'Vídeos informativos',
      loadingVideos: 'Carregando vídeos...',
      noVideosAvailable: 'Não há vídeos disponíveis',
      viewHistory: 'Ver histórico',
      resultsInProfile: 'Os resultados dos seus exames estão salvos no seu perfil',
      // Food recommendations translations
      recipesForAnemia: 'Essas receitas são especialmente selecionadas para combater a anemia e aumentar seus níveis de ferro.',
      recipesForRegion: 'Essas receitas são especialmente selecionadas para combater a anemia e aumentar seus níveis de ferro, com ênfase especial em pratos típicos de',
      setLocation: 'configurar sua localização no seu perfil',
      toSeeRelevantRecipes: 'Para ver receitas mais relevantes para sua região, convidamos você a',
      // Recipe translations in Portuguese
      recipes: {
        'Ensalada de espinacas con fresas': 'Salada de Espinafre com Morangos',
        'Una ensalada rica en hierro y vitamina C para mejorar la absorción del hierro.': 'Uma salada rica em ferro e vitamina C para melhorar a absorção do ferro.',
        'Hígado salteado con cebola y manzana': 'Fígado Salteado com Cebola e Maçã',
        'Una receta rica en hierro hemo, que es más fácilmente absorbido por el cuerpo.': 'Uma receita rica em ferro heme, que é mais facilmente absorvido pelo corpo.',
        'Lentejas con verduras y limón': 'Lentilhas com Legumes e Limão',
        'Un plato nutritivo rico en hierro, proteínas y vitamina C.': 'Um prato nutritivo rico em ferro, proteínas e vitamina C.',
        'Batido de remolacha y frutos rojos': 'Smoothie de Beterraba e Frutas Vermelhas',
        'Bebida energética que ayuda a aumentar los niveles de hemoglobina.': 'Bebida energética que ajuda a aumentar os níveis de hemoglobina.'
      },
      // Common UI translations
      error: 'Erro',
      loading: 'Carregando...',
      retry: 'Tentar novamente',
      save: 'Salvar',
      cancel: 'Cancelar',
      delete: 'Excluir',
      edit: 'Editar',
      search: 'Pesquisar',
      filter: 'Filtrar',
      sort: 'Ordenar',
      back: 'Voltar',
      profilePicture: 'Foto do Perfil',
      yes: 'Sim',
      no: 'Não',
      unknown: 'Desconhecido',
      question: 'Pergunta',
      of: 'de',
      profiles: 'Perfis',
      // Profile translations
      editProfile: 'Editar Perfil',
      saveChanges: 'Salvar Alterações',
      profileUpdated: 'Perfil atualizado com sucesso',
      // Error messages
      errorLoadingData: 'Erro ao carregar os dados',
      tryAgainLater: 'Por favor, tente novamente mais tarde',
      networkError: 'Erro de rede',
      // Success messages
      success: 'Sucesso',
      changesSaved: 'Alterações salvas com sucesso',
      // Validation messages
      requiredField: 'Este campo é obrigatório',
      invalidEmail: 'Endereço de e-mail inválido',
      invalidPhone: 'Número de telefone inválido',
      // Home screen translations
      home: {
        title: 'Anemia Detective',
        subtitle: 'Avaliação rápida e recomendações para pessoas com possível anemia',
        questionnaire: {
          title: 'Questionário de Sintomas',
          description: '5 perguntas rápidas sobre sinais de anemia'
        },
        photoEvaluation: {
          title: 'Avaliação por Fotos',
          description: 'Análise visual de olhos e unhas'
        },
        recommendations: 'Acessar recomendações',
        disclaimer: 'Este aplicativo não substitui um diagnóstico médico. Consulte um profissional para uma análise definitiva.'
      },
      questionnaire: 'Questionário',
      questionnaireQuestions: {
        '1': "Você se sente frequentemente cansado sem um motivo aparente?",
        '2': "Sua pele parece mais pálida do que o normal?",
        '3': "Você frequentemente sente tonturas ou fraqueza?",
        '4': "Suas unhas têm um tom pálido ou quebram facilmente?",
        '5': "O interior das suas pálpebras está mais branco do que o normal?"
      },
      results: {
        title: "Resultado",
        saveSuccess: "Resultados salvos com sucesso",
        saveError: "Erro ao salvar resultados: {{error}}",
        nextSteps: "Continuar com os próximos passos",
        backToHome: "Voltar ao início",
        highRisk: "Alto risco",
        moderateRisk: "Risco moderado",
        lowRisk: "Baixo risco",
        healthy: "Saudável",
        messages: {
          healthy: "Sua avaliação é favorável, mas manter uma dieta balanceada é sempre importante para prevenir a anemia.",
          moderate: "Sua avaliação mostra risco moderado. Sugerimos melhorar sua alimentação e incluir mais alimentos ricos em ferro.",
          high: "Com base na sua avaliação, há sinais que sugerem possível anemia. Recomendamos revisar uma dieta rica em ferro e consultar um médico.",
          short: {
            healthy: "Você está dentro da faixa saudável.",
            moderate: "Seu nível está ligeiramente baixo.",
            high: "Seu nível está criticamente baixo."
          }
        }
      },
      profile: {
        title: "Perfis",
        savedProfiles: "Perfis salvos",
        addProfile: "Adicionar perfil",
        anemiaStatus: "Estado da anemia",
        riskLevels: {
          high: "Alta probabilidade",
          moderate: "Risco moderado",
          low: "Baixo risco"
        },
        location: {
          country: "País",
          region: "Região",
          selectCountry: "Selecionar país",
          selectRegion: "Selecionar região",
          loading: "Carregando...",
          updateSuccess: "Localização atualizada",
          updateError: "Erro ao atualizar localização: {{error}}",
          loadCountriesError: "Erro ao carregar países: {{error}}",
          loadRegionsError: "Erro ao carregar regiões: {{error}}",
          title: "Localização"
        },
        additionalOptions: {
          title: "Opções adicionais",
          startNewEvaluation: "Iniciar nova avaliação",
          signOut: "Sair"
        },
        newProfile: "Novo perfil",
        name: "Nome",
        fullName: "Nome completo",
        age: "Idade",
        saveProfile: "Salvar perfil",
        noProfiles: "Não há perfis salvos",
        loginToManage: "Faça login para salvar e gerenciar mais perfis",
        profileSelected: "Perfil \"{{name}}\" selecionado",
        invalidProfileId: "ID de perfil inválido",
        years: "anos",
        delete: "Excluir",
        edit: "Editar",
        auth: {
          title: "Entrar (opcional)",
          description: "Você pode entrar para salvar seu perfil entre dispositivos.",
          signInWithGoogle: "Continuar com Google",
          signedInAs: "Conectado como:",
          signOut: "Sair"
        },
        evaluationHistory: {
          title: "Histórico de Avaliações",
          loading: "Carregando histórico...",
          noEvaluations: "Não há avaliações anteriores para este perfil",
          viewAll: "Ver todas",
          loadError: "Erro ao carregar histórico: {{error}}",
          location: {
            latitude: "Lat",
            longitude: "Lon",
            notAvailable: "N/D"
          }
        },
        selector: {
          loading: "Carregando perfis...",
          noProfiles: "Não há perfis disponíveis",
          selectProfile: "Selecionar perfil",
          ageFormat: "{{age}} anos"
        },
        healthPoints: {
          title: "Pontos de Saúde",
          level: "Nível {{level}}",
          progress: "Progresso",
          points: "{{points}} pontos",
          description: "Ganhe pontos consumindo alimentos saudáveis"
        },
        newEvaluation: "Nova avaliação",
        performNewEvaluation: "Realizar nova avaliação"
      },
      photoEvaluation: {
        title: "Avaliação por fotos",
        errors: {
          loadSampleImage: "Erro ao carregar a imagem de amostra",
          analyzeImage: "Erro ao analisar a imagem",
          takeEyePhoto: "Por favor, tire uma foto do seu olho primeiro",
          processEyeImage: "Ocorreu um erro ao processar a imagem do olho",
          takeNailPhoto: "Por favor, tire uma foto das suas unhas primeiro",
          processImage: "Ocorreu um erro ao processar a imagem"
        },
        analysisDetails: {
          title: "Detalhes da análise",
          description: "Resultados detalhados da análise de imagens",
          viewDetails: "Ver detalhes da análise",
          close: "Fechar",
          tabs: {
            eyes: "Olhos",
            nails: "Unhas"
          },
          conjunctiva: {
            title: "Análise da conjuntiva",
            detectedPredictions: "Previsões detectadas:",
            noElements: "Nenhum elemento detectado na imagem",
            confidence: "confiança",
            location: "Localização"
          },
          nails: {
            title: "Análise das unhas",
            detectedPredictions: "Previsões detectadas:",
            noElements: "Nenhum elemento detectado na imagem",
            confidence: "confiança",
            location: "Localização"
          },
          processingTime: "Tempo de processamento",
          model: "Modelo"
        },
        profileSelect: {
          title: "Selecione um perfil",
          description: "Selecione o perfil para o qual deseja realizar a avaliação ou continue sem fazer login",
          manageProfiles: "Gerenciar perfis",
          continueWithoutLogin: "Continuar sem fazer login"
        },
        sampleImages: {
          title: "Imagens de amostra:",
          alt: "Amostra {{number}}",
          hide: "Ocultar imagens de amostra",
          show: "Ver imagens de amostra"
        },
        buttons: {
          continue: "Continuar",
          analyze: "Analisar imagens"
        },
        processing: {
          title: "Analisando imagens",
          description: "Nosso sistema está processando as fotografias para detectar possíveis sinais de anemia..."
        },
        camera: {
          eyeTitle: "Fotografia do olho",
          eyeInstructions: "Tire uma foto do interior da sua pálpebra inferior. Puxe suavemente a pálpebra para mostrar a área interna.",
          eyeAlt: "Olho capturado",
          nailTitle: "Fotografia das unhas",
          nailInstructions: "Tire uma foto clara das suas unhas com boa iluminação. Coloque sua mão em uma superfície plana.",
          nailAlt: "Unhas capturadas",
          tapToCapture: "Toque para capturar imagem",
          loadAnotherImage: "Carregar outra imagem"
        },
        modelSelector: {
          title: "Selecionar Modelo de Análise:",
          model2: "Modelo 2 (Detect)",
          model3Server: "Modelo 3 (Servidor)",
          model3Live: "Modelo 3 (Live)",
          currentModel: "Modelo atual:",
          show: "⚙️ Selecionar modelo",
          hide: "⚙️ Ocultar seleção"
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    lng: 'es', // Forzamos español como idioma por defecto
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage']
    }
  });

export default i18n;
